import { split, tail, head, isEmpty } from 'ramda';
import { GraphQLClient } from 'src/graphqlhooks';
import TokenGenerator from 'src/tokenGenerator/graphql';
import axios from 'axios';
import { createClient } from 'graphql-ws';
import { buildAxiosFetch } from './axios-fetch';

/**
 * The GraphQLFactory
 * Sets up the graphql client and manages the JWT Token using TokenGen
 *
 * Usually this is a single instance attached at the top of the parent component
 *
 */
class GraphQLFactory {
  name: string;
  api: string;
  tokenGen: any;

  constructor(theName: string, api: any) {
    this.name = theName;
    this.api = api;
    this.tokenGen = TokenGenerator.getInstance();
  }

  /** This is method create a new GraphQLClient **/
  create() {
    return new GraphQLClient({
      url: this.api,
      fetch: this.mkAxiosInterceptor(),
      subscriptionClient: this.mkSubcriptionClient(),
    });
  }

  /**
   * Setup an axios interceptor callback.
   * The callback is function is called for every request.
   */
  mkAxiosInterceptor() {
    const gqlAxios = axios.create();
    gqlAxios.interceptors.request.use(async (config) => {
      const token = await this.tokenGen.getToken()
      const result = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      };
      return result;
    });

    return buildAxiosFetch(gqlAxios);
  }

  mkSubcriptionClient() {
    // non-fatal WebSocket connection close events will cause the
    // client to automatically reconnect. the retries are silent, meaning
    // that the client will not error out unless the retry attempts have been
    // exceeded or the close event was fatal (read more about the fatal
    // close events in the documentation). additionally, all active subscriptions
    // will automatically resubscribe upon successful reconnect. this behaviour
    // can be leveraged to implement a secure and sound way of authentication;
    // handling server-side validation, expiry indication and timely token refreshes

    // indicates that the server closed the connection because of
    // an auth problem. it indicates that the token should refresh
    let shouldRefreshToken = true;
    let hasuraToken = ''
    return createClient({
      url: `wss:${this.splitHttps(this.api)}`,
      lazy: true,
      shouldRetry: () => true,
      connectionParams: async () => {

        if (shouldRefreshToken || isEmpty(hasuraToken)) {
          // refresh the token because it is no longer valid
          // and reset the flag to avoid refreshing too many times
          hasuraToken = await this.tokenGen.getToken();
          shouldRefreshToken = false;
        }
        return {
          headers: {
            Authorization: `Bearer ${hasuraToken}`,
          },
        };
      },
      on: {
        connected: (socket) => { },
        closed: (event) => {
          shouldRefreshToken = true;
        },
      },
    });
  }

  splitHttps(endPointUrl: any) {
    return head(tail(split(':', endPointUrl)));
  }
}

export default GraphQLFactory;
